import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  Undo,
  OpenInNew,
  Public,
  Remove,
} from '@mui/icons-material'
import { NEWS_REVIEW_STATUS } from '../../../constants/newsReviewStatus'

const getRelativeTime = (date) => {
  if (!date) return ''
  let d
  if (date instanceof Date) {
    d = date
  } else {
    const s = typeof date === 'string' && !/[Zz]|[+-]\d{2}:?\d{2}$/.test(date) ? date + 'Z' : date
    d = new Date(s)
  }
  if (isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  const sec = Math.floor(diffMs / 1000)
  if (sec < 60) return 'Just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} minute${min !== 1 ? 's' : ''} ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr} hour${hr !== 1 ? 's' : ''} ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day} day${day !== 1 ? 's' : ''} ago`
  const week = Math.floor(day / 7)
  if (week < 5) return `${week} week${week !== 1 ? 's' : ''} ago`
  const month = Math.floor(day / 30)
  if (month < 12) return `${month} month${month !== 1 ? 's' : ''} ago`
  const year = Math.floor(day / 365)
  return `${year} year${year !== 1 ? 's' : ''} ago`
}

const GCC_COUNTRIES = ['Bahrain', 'KSA', 'Kuwait', 'Oman', 'Qatar', 'UAE']

const GCC_FLAG_CODES = {
  Bahrain: 'bh', KSA: 'sa', Kuwait: 'kw', Oman: 'om', Qatar: 'qa', UAE: 'ae',
}

const themeColors = {
  primary: '#232536',
  secondary: '#ffcf51',
  hover: '#3a3e5a',
}

// Confidence score coloring
// 0–39: Very Low (auto-rejected), 40–75: Low (pending review), 76–100: Below Threshold (auto-include)
export const getConfidenceColor = (score) => {
  if (score <= 39) return { bg: '#fef2f2', border: '#fca5a5', text: '#dc2626', label: 'Very Low' }
  if (score <= 75) return { bg: '#fff7ed', border: '#fdba74', text: '#ea580c', label: 'Low' }
  return { bg: '#fefce8', border: '#fde047', text: '#ca8a04', label: 'Below Threshold' }
}

// Review status chip
const StatusChip = ({ status }) => {
  const map = {
    [NEWS_REVIEW_STATUS.PENDING_REVIEW]: { label: 'Pending Review', color: 'warning' },
    [NEWS_REVIEW_STATUS.APPROVED]:       { label: 'Approved',        color: 'success' },
    [NEWS_REVIEW_STATUS.REJECTED]:       { label: 'Rejected',        color: 'error' },
    [NEWS_REVIEW_STATUS.AUTO_INCLUDE]:   { label: 'Auto-Included',   color: 'info' },
    [NEWS_REVIEW_STATUS.AUTO_REJECTED]:  { label: 'Auto-Rejected',   color: 'error' },
  }
  const cfg = map[status] || { label: status, color: 'default' }
  return <Chip label={cfg.label} color={cfg.color} size='small' sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
}

// Country editor
const CountryEditor = ({ countries = [], onUpdate, disabled }) => {
  const [addValues, setAddValues] = useState([])
  const available = GCC_COUNTRIES.filter((c) => !countries.includes(c))

  const handleClose = () => {
    if (addValues.length === 0) return
    onUpdate([...countries, ...addValues.filter((v) => !countries.includes(v))])
    setAddValues([])
  }

  const handleRemove = (c) => {
    onUpdate(countries.filter((x) => x !== c))
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 0.5,
        overflowX: 'auto',
        overflowY: 'hidden',
        maxWidth: '100%',
        '&::-webkit-scrollbar': { height: 4 },
      }}
    >
      {countries.map((c) => (
        <Chip
          key={c}
          label={c}
          size='small'
          icon={<Public sx={{ fontSize: '0.8rem !important' }} />}
          onDelete={disabled ? undefined : () => handleRemove(c)}
          deleteIcon={<Remove sx={{ fontSize: '0.75rem !important' }} />}
          sx={{
            bgcolor: '#e0f2fe',
            color: '#0369a1',
            border: '1px solid #bae6fd',
            '& .MuiChip-deleteIcon': { color: '#0369a1' },
            fontSize: '0.72rem',
          }}
        />
      ))}
      {!disabled && available.length > 0 && (
        <Select
          size='small'
          multiple
          value={addValues}
          displayEmpty
          onChange={(e) => setAddValues(e.target.value)}
          onClose={handleClose}
          sx={{
            fontSize: '0.72rem',
            height: 24,
            minWidth: 88,
            '& .MuiSelect-select': { py: '2px', px: 1 },
          }}
          renderValue={() => <span style={{ color: '#6b7280' }}>+ Add</span>}
          MenuProps={{ disableScrollLock: true }}
        >
          {available.map((c) => (
            <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem', py: 0.5, display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={addValues.includes(c)}
                size='small'
                sx={{ p: 0.5, '&.Mui-checked': { color: '#0369a1' } }}
              />
              {GCC_FLAG_CODES[c] && (
                <img
                  loading='lazy'
                  width='18'
                  src={`https://flagcdn.com/w20/${GCC_FLAG_CODES[c]}.png`}
                  srcSet={`https://flagcdn.com/w40/${GCC_FLAG_CODES[c]}.png 2x`}
                  alt=''
                  style={{ flexShrink: 0, marginRight: 4 }}
                />
              )}
              <ListItemText primary={c} primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </MenuItem>
          ))}
        </Select>
      )}
    </Box>
  )
}

// Main Card
const NewsArticleCard = ({
  article,
  selected,
  onToggleSelect,
  onApprove,
  onReject,
  onUndo,
  onUpdateCountries,
  disabled = false,
}) => {
  const {
    id,
    title,
    description,
    source,
    pubDate,
    countries = [],
    newsConfidence,
    reviewStatus,
    reviewedBy,
    reviewedAt,
    matchedKeywords = [],
    link,
  } = article

  const confidenceColor = getConfidenceColor(newsConfidence ?? 0)
  const canUndo = reviewStatus === NEWS_REVIEW_STATUS.APPROVED || reviewStatus === NEWS_REVIEW_STATUS.REJECTED
  const isActioned = canUndo

  return (
    <Card
      elevation={0}
      sx={{
        border: '1.5px solid',
        borderColor: selected ? themeColors.primary : '#e2e8f0',
        borderRadius: 3,
        transition: 'all 0.2s ease',
        position: 'relative',
        width: '100%',
        height: 365,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: selected ? themeColors.primary : '#cbd5e1',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transform: 'translateY(-1px)',
        },
        ...(isActioned && {
          opacity: 0.75,
          '&:hover': { opacity: 1, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' },
        }),
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 }, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        {/* Row 1: Checkbox + Confidence + Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Checkbox
            checked={selected}
            onChange={() => onToggleSelect(id)}
            size='small'
            sx={{
              p: 0,
              color: '#94a3b8',
              '&.Mui-checked': { color: themeColors.primary },
            }}
          />
          {/* Confidence badge */}
          <Chip
            label={`${newsConfidence ?? '-'}`}
            size='small'
            sx={{
              bgcolor: confidenceColor.bg,
              border: `1px solid ${confidenceColor.border}`,
              color: confidenceColor.text,
              fontWeight: 700,
              fontSize: '0.78rem',
              height: 22,
            }}
          />
          <Typography
            variant='caption'
            sx={{ color: confidenceColor.text, fontWeight: 500, fontSize: '0.7rem' }}
          >
            {confidenceColor.label}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <StatusChip status={reviewStatus} />
          {link && (
            <Tooltip title='Open original article'>
              <IconButton
                size='small'
                component='a'
                href={link}
                target='_blank'
                rel='noopener noreferrer'
                sx={{ color: '#64748b', p: 0.25, '&:hover': { color: themeColors.primary } }}
              >
                <OpenInNew sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Title */}
        <Typography
          variant='subtitle2'
          sx={{
            fontWeight: 700,
            fontSize: '0.92rem',
            color: themeColors.primary,
            mb: 0.75,
            lineHeight: 1.4,
            minHeight: '2.58rem',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {title || '(No title)'}
        </Typography>

        {/* Description */}
        <Box sx={{ minHeight: '3.85rem', mb: 1.25 }}>
          {description && (
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{
                fontSize: '0.82rem',
                lineHeight: 1.55,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
              }}
            >
              {description}
            </Typography>
          )}
        </Box>

        {/* Countries */}
        <Box sx={{ mb: 1, minHeight: 53 }}>
          <Typography
            variant='caption'
            sx={{ color: '#6b7280', fontWeight: 600, mb: 0.4, display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
          >
            Countries
          </Typography>
          <CountryEditor
            countries={countries}
            onUpdate={(updated) => onUpdateCountries(id, updated)}
            disabled={disabled}
          />
        </Box>

        {/* Meta: Source & Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, minHeight: 18, overflow: 'hidden' }}>
          {source && (
            <Typography
              variant='caption'
              sx={{
                color: '#475569',
                fontSize: '0.75rem',
                fontStyle: 'italic',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                maxWidth: '72%',
              }}
            >
              {source}
            </Typography>
          )}
          {source && pubDate && (
            <Box component='span' sx={{ mx: 0.2, color: '#cbd5e1', fontSize: '0.75rem', lineHeight: 1 }}>·</Box>
          )}
          {pubDate && (
            <Typography variant='caption' sx={{ color: '#94a3b8', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
              {getRelativeTime(pubDate)}
            </Typography>
          )}
        </Box>

        {/* Keywords */}
        <Box sx={{ minHeight: 22, mb: 1.5, overflow: 'hidden' }}>
          {matchedKeywords.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5, overflowX: 'auto', overflowY: 'hidden' }}>
              {matchedKeywords.slice(0, 4).map((kw) => (
                <Chip
                  key={kw}
                  label={kw}
                  size='small'
                  sx={{
                    bgcolor: '#f1f5f9',
                    color: '#475569',
                    fontSize: '0.68rem',
                    height: 20,
                    border: '1px solid #e2e8f0',
                    flexShrink: 0,
                  }}
                />
              ))}
              {matchedKeywords.length > 4 && (
                <Chip
                  label={`+${matchedKeywords.length - 4}`}
                  size='small'
                  sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.68rem', height: 20, flexShrink: 0 }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pt: 1,
            mt: 'auto',
            borderTop: '1px solid #f1f5f9',
          }}
        >
          {canUndo ? (
            <>
              <Tooltip title='Undo'>
                <IconButton
                  size='small'
                  onClick={() => onUndo(id)}
                  disabled={disabled}
                  sx={{ color: '#6b7280', '&:hover': { color: '#374151' } }}
                >
                  <Undo sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              {(reviewedBy || reviewedAt) && (
                <Typography variant='caption' sx={{ color: '#64748b', fontSize: '0.68rem', fontStyle: 'italic', ml: 0.5 }}>
                  {reviewStatus === NEWS_REVIEW_STATUS.APPROVED ? 'Approved' : 'Rejected'}
                  {reviewedBy ? ` by ${reviewedBy}` : ''}
                  {reviewedAt ? ` · ${getRelativeTime(reviewedAt)}` : ''}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Tooltip title='Approve'>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => onApprove(id)}
                    disabled={disabled}
                    sx={{
                      color: '#16a34a',
                      bgcolor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      mr: 0.5,
                      '&:hover': { bgcolor: '#dcfce7', borderColor: '#86efac' },
                      '&.Mui-disabled': { opacity: 0.4 },
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Reject'>
                <span>
                  <IconButton
                    size='small'
                    onClick={() => onReject(id)}
                    disabled={disabled}
                    sx={{
                      color: '#dc2626',
                      bgcolor: '#fef2f2',
                      border: '1px solid #fecaca',
                      '&:hover': { bgcolor: '#fee2e2', borderColor: '#fca5a5' },
                      '&.Mui-disabled': { opacity: 0.4 },
                    }}
                  >
                    <Cancel sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default NewsArticleCard
